import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  FiCalendar, FiChevronLeft, FiChevronRight, FiPlus, FiX, FiTrash2, FiSend,
  FiCopy, FiClock, FiUsers, FiCheck, FiRepeat, FiInbox, FiSave,
} from 'react-icons/fi';
import { loadTable, addRow, saveRow, removeRow, getTableData } from '../db-viewer/data/dataAccess';
import SearchableDropdown, { DropdownOption } from '../../components/ui/SearchableDropdown';
import { addNotification } from '../../lib/notifications';
import './SchedulingPage.css';

// =============================================
// Staff Scheduling (Phase 1) — a new pillar built off the business Employees
// roster (migration 102) + sched_shift / sched_swap_request (migration 103).
//   • Manager view: week grid, add/edit/delete shifts, publish, copy week, swap inbox.
//   • Employee "My Schedule": own published shifts, give away, pick up open shifts.
// The logged-in user is matched to roster employees by EMAIL.
// =============================================

// ---- date helpers ----
const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseYmd = (s: string) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, (m || 1) - 1, d || 1); };
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
/** Monday of the week containing dateStr. */
function mondayOf(dateStr: string): Date {
  const d = parseYmd(dateStr);
  const dow = (d.getDay() + 6) % 7; // 0=Mon
  return addDays(d, -dow);
}
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const fmtTime = (t: string | null | undefined) => (t ? String(t).slice(0, 5) : '');
const fmtDayLabel = (d: Date) => `${DAY_NAMES[(d.getDay() + 6) % 7]} ${d.getMonth() + 1}/${d.getDate()}`;
const todayYmd = () => ymd(new Date());

/** Duration in hours for a shift (end − start − break), handling overnight. */
function shiftHours(s: { start_time?: any; end_time?: any; break_minutes?: any }): number {
  const st = fmtTime(s.start_time), en = fmtTime(s.end_time);
  if (!st || !en) return 0;
  const [sh, sm] = st.split(':').map(Number);
  const [eh, em] = en.split(':').map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;               // overnight
  mins -= Number(s.break_minutes) || 0;
  return Math.max(0, Math.round((mins / 60) * 100) / 100);
}

const LOC_COLORS = ['#1565c0', '#2e7d32', '#c62828', '#6a1b9a', '#e65100', '#00838f', '#4e342e', '#ad1457'];
const colorForLocation = (locId: any, unitId: any) => {
  const k = Number(unitId != null ? unitId * 7 : locId != null ? locId : 0);
  return LOC_COLORS[k % LOC_COLORS.length];
};

interface CurrentUser { user_id: number; email: string; first_name: string; owner_id: number | null; }
function resolveCurrentUser(users: Record<string, any>[]): CurrentUser | null {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return null;
    const id = Number(JSON.parse(raw).id || JSON.parse(raw).user_id);
    const u = users.find(x => Number(x.user_id) === id);
    if (!u) return null;
    return { user_id: id, email: (u.email || '').toLowerCase(), first_name: u.first_name || '', owner_id: (u.owner_id == null || u.owner_id === '') ? null : Number(u.owner_id) };
  } catch { return null; }
}

const NOW_ISO = () => new Date().toISOString().replace('T', ' ').substring(0, 19);
const empName = (e: any) => (e?.emp_nickname?.trim() || e?.emp_name?.trim() || (e ? `Emp ${e.employee_id}` : 'Open shift'));

const OT_THRESHOLD = 40; // hours/week before overtime is flagged
const money = (n: number) => n.toLocaleString(undefined, { style: 'currency', currency: 'USD' }).replace('$', '$');
const dateInRange = (d: string, a: string, b: string) => !!d && d >= a && d <= b;

/** Build an .ics calendar file for a set of shifts. */
function buildIcs(rows: Record<string, any>[], summaryFor: (s: any) => string): string {
  const esc = (s: string) => String(s).replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Akountant//Scheduling//EN', 'CALSCALE:GREGORIAN'];
  rows.forEach(s => {
    const d = (s.shift_date || '').slice(0, 10).replace(/-/g, '');
    const st = fmtTime(s.start_time).replace(':', '') + '00';
    const en = fmtTime(s.end_time).replace(':', '') + '00';
    if (!d || st.length < 6 || en.length < 6) return;
    lines.push('BEGIN:VEVENT', `UID:shift-${s.shift_id}@akountant`, `DTSTART:${d}T${st}`, `DTEND:${d}T${en}`, `SUMMARY:${esc(summaryFor(s))}`, 'END:VEVENT');
  });
  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}

interface ShiftDraft {
  shift_id?: number;
  employee_id: string;   // '' = open shift
  location_id: string;   // 'loc:x' | 'unit:y' | ''
  shift_date: string;
  start_time: string;
  end_time: string;
  break_minutes: string;
  role_label: string;
  notes: string;
  repeat_weeks: string;   // "1" = just this week (new shifts only)
}

const SchedulingPage: React.FC = () => {
  const [loaded, setLoaded] = useState(false);
  const [users, setUsers] = useState<Record<string, any>[]>([]);
  const [businesses, setBusinesses] = useState<Record<string, any>[]>([]);
  const [employees, setEmployees] = useState<Record<string, any>[]>([]);
  const [, setEmpLocations] = useState<Record<string, any>[]>([]);
  const [locations, setLocations] = useState<Record<string, any>[]>([]);
  const [units, setUnits] = useState<Record<string, any>[]>([]);
  const [shifts, setShifts] = useState<Record<string, any>[]>([]);
  const [swaps, setSwaps] = useState<Record<string, any>[]>([]);
  const [timeOff, setTimeOff] = useState<Record<string, any>[]>([]);
  const [availability, setAvailability] = useState<Record<string, any>[]>([]);
  const [templates, setTemplates] = useState<Record<string, any>[]>([]);
  const [templateShifts, setTemplateShifts] = useState<Record<string, any>[]>([]);

  const [mode, setMode] = useState<'manage' | 'mine'>('manage');
  const [selectedBizId, setSelectedBizId] = useState<string>('');
  const [weekAnchor, setWeekAnchor] = useState<string>(todayYmd());
  const [editor, setEditor] = useState<ShiftDraft | null>(null);
  const [toast, setToast] = useState<string>('');
  const [toForm, setToForm] = useState<{ business_id: string; start: string; end: string; reason: string }>({ business_id: '', start: '', end: '', reason: '' });
  const [availBizId, setAvailBizId] = useState<string>('');
  const [templateName, setTemplateName] = useState('');
  const [applyTemplateId, setApplyTemplateId] = useState('');
  const [dragShiftId, setDragShiftId] = useState<number | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string>('');

  const flash = useCallback((m: string) => { setToast(m); setTimeout(() => setToast(''), 4500); }, []);

  const reload = useCallback(async () => {
    const [u, b, e, el, loc, un, sh, sw, to, av, tp, tps] = await Promise.all([
      loadTable('users'), loadTable('user_businesses'),
      loadTable('user_business_employee'), loadTable('user_business_employee_location'),
      loadTable('user_business_location'), loadTable('user_business_unit'),
      loadTable('sched_shift'), loadTable('sched_swap_request'),
      loadTable('sched_time_off').catch(() => [] as any[]),
      loadTable('sched_availability').catch(() => [] as any[]),
      loadTable('sched_template').catch(() => [] as any[]),
      loadTable('sched_template_shift').catch(() => [] as any[]),
    ]);
    setUsers(u); setBusinesses(b); setEmployees(e); setEmpLocations(el);
    setLocations(loc); setUnits(un); setShifts(sh); setSwaps(sw); setTimeOff(to);
    setAvailability(av); setTemplates(tp); setTemplateShifts(tps);
    setLoaded(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    reload().catch(() => {
      if (cancelled) return;
      setUsers(getTableData('users')); setBusinesses(getTableData('user_businesses'));
      setEmployees(getTableData('user_business_employee')); setEmpLocations(getTableData('user_business_employee_location'));
      setLocations(getTableData('user_business_location')); setUnits(getTableData('user_business_unit'));
      setShifts(getTableData('sched_shift')); setSwaps(getTableData('sched_swap_request'));
      setTimeOff(getTableData('sched_time_off'));
      setAvailability(getTableData('sched_availability'));
      setTemplates(getTableData('sched_template'));
      setTemplateShifts(getTableData('sched_template_shift'));
      setLoaded(true);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentUser = useMemo(() => loaded ? resolveCurrentUser(users) : null, [users, loaded]);

  // Businesses this user owns (they're the account owner).
  const ownedBusinesses = useMemo(() => {
    if (!currentUser) return [];
    const ownerId = currentUser.owner_id == null ? currentUser.user_id : currentUser.owner_id;
    // eslint-disable-next-line eqeqeq
    return businesses.filter(b => b.owner_id == ownerId && b.is_deleted != 1 && b.is_client_business != 1);
  }, [businesses, currentUser]);

  // Roster employee rows that are ME (matched by email), across any business.
  const myEmployeeRows = useMemo(() => {
    if (!currentUser?.email) return [];
    return employees.filter(e => e.is_deleted != 1 && (e.email || '').toLowerCase() === currentUser.email);
  }, [employees, currentUser]);
  const myEmployeeIds = useMemo(() => new Set(myEmployeeRows.map(e => Number(e.employee_id))), [myEmployeeRows]);

  const isManager = ownedBusinesses.length > 0;
  const isEmployee = myEmployeeRows.length > 0;

  // Land in the sensible default mode once we know who this is.
  useEffect(() => {
    if (!loaded || !currentUser) return;
    setMode(isManager ? 'manage' : 'mine');
  }, [loaded, currentUser, isManager]);

  useEffect(() => {
    if (!selectedBizId && ownedBusinesses.length) setSelectedBizId(String(ownedBusinesses[0].business_id));
  }, [ownedBusinesses, selectedBizId]);

  const bizId = Number(selectedBizId) || 0;

  // ---- lookups ----
  const bizEmployees = useMemo(() => employees
    // eslint-disable-next-line eqeqeq
    .filter(e => e.business_id == bizId && e.is_deleted != 1)
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0)), [employees, bizId]);
  const empById = useMemo(() => new Map(employees.map(e => [Number(e.employee_id), e])), [employees]);
  const locById = useMemo(() => new Map(locations.map(l => [Number(l.location_id), l])), [locations]);
  const unitById = useMemo(() => new Map(units.map(u => [Number(u.unit_id), u])), [units]);

  const locLabel = useCallback((locId: any, unitId: any) => {
    if (unitId != null) {
      const u = unitById.get(Number(unitId));
      const l = u ? locById.get(Number(u.location_id)) : null;
      return `${l ? (l.loc_name || 'Location') + ' › ' : ''}${u ? (u.unit_name || 'Unit') : ''}`;
    }
    if (locId != null) { const l = locById.get(Number(locId)); return l ? (l.loc_name || 'Location') : ''; }
    return '';
  }, [locById, unitById]);

  // Location/unit options for the selected business.
  const locationOptions = useMemo<DropdownOption[]>(() => {
    const opts: DropdownOption[] = [];
    locations
      // eslint-disable-next-line eqeqeq
      .filter(l => l.business_id == bizId && l.is_deleted != 1)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      .forEach(l => {
        opts.push({ value: `loc:${l.location_id}`, label: l.loc_name || `Location ${l.location_id}`, depth: 0 });
        units
          // eslint-disable-next-line eqeqeq
          .filter(u => u.location_id == l.location_id && u.is_deleted != 1)
          .forEach(u => opts.push({ value: `unit:${u.unit_id}`, label: `${l.loc_name || 'Location'} › ${u.unit_name || `Unit ${u.unit_id}`}`, depth: 1 }));
      });
    return opts;
  }, [locations, units, bizId]);

  const employeeOptions = useMemo<DropdownOption[]>(() =>
    bizEmployees.map(e => ({ value: String(e.employee_id), label: `${empName(e)}${e.position ? ' · ' + e.position : ''}` })), [bizEmployees]);

  // ---- week ----
  const monday = useMemo(() => mondayOf(weekAnchor), [weekAnchor]);
  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(monday, i)), [monday]);
  const weekYmds = useMemo(() => weekDates.map(ymd), [weekDates]);
  const weekLabel = `${weekDates[0].getMonth() + 1}/${weekDates[0].getDate()} – ${weekDates[6].getMonth() + 1}/${weekDates[6].getDate()}, ${weekDates[6].getFullYear()}`;

  // Shifts for the selected business within the visible week.
  const weekShifts = useMemo(() => shifts.filter(s =>
    // eslint-disable-next-line eqeqeq
    s.business_id == bizId && s.is_deleted != 1 && s.status !== 'cancelled' &&
    weekYmds.includes((s.shift_date || '').slice(0, 10))), [shifts, bizId, weekYmds]);

  const shiftsForCell = useCallback((empId: number | null, dateY: string) =>
    weekShifts.filter(s => (empId == null ? s.employee_id == null : Number(s.employee_id) === empId) && (s.shift_date || '').slice(0, 10) === dateY)
      .sort((a, b) => fmtTime(a.start_time).localeCompare(fmtTime(b.start_time))), [weekShifts]);

  const weeklyHours = useCallback((empId: number) =>
    weekShifts.filter(s => Number(s.employee_id) === empId).reduce((sum, s) => sum + shiftHours(s), 0), [weekShifts]);
  const dailyCoverage = useCallback((dateY: string) =>
    weekShifts.filter(s => (s.shift_date || '').slice(0, 10) === dateY && s.employee_id != null).length, [weekShifts]);

  // ---- editor ----
  const openNewShift = useCallback((empId: number | null, dateY: string) => {
    setEditor({ employee_id: empId == null ? '' : String(empId), location_id: locationOptions[0]?.value || '', shift_date: dateY, start_time: '09:00', end_time: '17:00', break_minutes: '0', role_label: '', notes: '', repeat_weeks: '1' });
  }, [locationOptions]);
  const openEditShift = useCallback((s: Record<string, any>) => {
    setEditor({
      shift_id: s.shift_id,
      employee_id: s.employee_id == null ? '' : String(s.employee_id),
      location_id: s.unit_id != null ? `unit:${s.unit_id}` : (s.location_id != null ? `loc:${s.location_id}` : ''),
      shift_date: (s.shift_date || '').slice(0, 10),
      start_time: fmtTime(s.start_time) || '09:00',
      end_time: fmtTime(s.end_time) || '17:00',
      break_minutes: String(s.break_minutes ?? 0),
      role_label: s.role_label || '',
      notes: s.notes || '',
      repeat_weeks: '1',
    });
  }, []);

  const decodeLoc = (v: string) => v.startsWith('unit:') ? { location_id: (() => { const u = unitById.get(Number(v.slice(5))); return u ? Number(u.location_id) : null; })(), unit_id: Number(v.slice(5)) } : v.startsWith('loc:') ? { location_id: Number(v.slice(4)), unit_id: null } : { location_id: null, unit_id: null };

  const saveShift = useCallback(async () => {
    if (!editor) return;
    const { location_id, unit_id } = decodeLoc(editor.location_id);
    const fields: Record<string, any> = {
      business_id: bizId,
      employee_id: editor.employee_id ? Number(editor.employee_id) : null,
      location_id, unit_id,
      shift_date: editor.shift_date,
      start_time: editor.start_time || null,
      end_time: editor.end_time || null,
      break_minutes: Number(editor.break_minutes) || 0,
      role_label: editor.role_label.trim() || null,
      notes: editor.notes.trim() || null,
      updated_at: NOW_ISO(),
    };
    try {
      if (editor.shift_id) {
        await saveRow('sched_shift', editor.shift_id, fields, 'shift_id');
      } else {
        const repeat = Math.max(1, Math.min(52, Number(editor.repeat_weeks) || 1));
        const base = parseYmd(editor.shift_date);
        for (let i = 0; i < repeat; i++) {
          await addRow('sched_shift', {
            ...fields, shift_date: ymd(addDays(base, i * 7)),
            status: editor.employee_id ? 'draft' : 'open', is_active: 1, is_deleted: 0,
            created_by: currentUser?.user_id ?? null, created_at: NOW_ISO(),
          }, 'shift_id');
        }
        if (repeat > 1) flash(`Created ${repeat} weekly shifts.`);
      }
      setEditor(null);
      await reload();
    } catch (e: any) { flash(e?.message || 'Could not save the shift.'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, bizId, currentUser, reload, flash]);

  const deleteShift = useCallback(async (shiftId: number) => {
    try { await removeRow('sched_shift', shiftId, 'shift_id'); setEditor(null); await reload(); }
    catch (e: any) { flash(e?.message || 'Could not delete.'); }
  }, [reload, flash]);

  // ---- publish week ----
  const publishWeek = useCallback(async () => {
    const drafts = weekShifts.filter(s => s.status === 'draft' && s.employee_id != null);
    if (drafts.length === 0) { flash('No draft shifts in this week to publish.'); return; }
    const now = NOW_ISO();
    for (const s of drafts) {
      try { await saveRow('sched_shift', s.shift_id, { status: 'published', published_at: now, updated_at: now }, 'shift_id'); } catch { /* ignore */ }
    }
    // Best-effort in-app notify for employees who have a linked login; email is queued (Phase 2 provider).
    const notified = new Set<number>();
    for (const s of drafts) {
      const emp = empById.get(Number(s.employee_id));
      const linked = emp && users.find(u => (u.email || '').toLowerCase() === (emp.email || '').toLowerCase());
      if (linked && !notified.has(Number(linked.user_id)) && currentUser) {
        notified.add(Number(linked.user_id));
        try {
          addNotification({
            account_owner_id: Number(linked.user_id), type: 'schedule_published',
            title: 'Schedule published', message: `Your schedule for ${weekLabel} is posted.`,
            actor_user_id: currentUser.user_id, actor_name: currentUser.first_name,
          });
        } catch { /* ignore */ }
      }
    }
    // Ask the server to email each assigned employee a link (provider-agnostic mailer:
    // queued/logged until MAIL_PROVIDER + creds are set, real send after).
    const link = `${window.location.origin}/dashboard/scheduling?wk=${weekYmds[0]}`;
    const recipients = Array.from(new Map(drafts.map((s): [string, { email: string; name: string } | null] => {
      const e = empById.get(Number(s.employee_id));
      return e && e.email ? [String(e.email).toLowerCase(), { email: e.email, name: empName(e) }] : ['', null];
    }).filter(x => x[0])).values()).filter(Boolean);
    const businessName = ownedBusinesses.find(b => Number(b.business_id) === bizId)?.busi_legal_name || '';
    let mail: any = { provider: 'none', sent: 0, queued: recipients.length, failed: 0 };
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const res = await fetch(`${API_BASE}/scheduling/publish-notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('akt_auth_token')}` },
        body: JSON.stringify({ weekLabel, link, businessName, recipients }),
      });
      if (res.ok) mail = await res.json();
    } catch { /* offline — the schedule is still published */ }
    await reload();
    const emailMsg = mail.provider === 'none'
      ? `${mail.queued} email${mail.queued === 1 ? '' : 's'} queued (set MAIL_PROVIDER to send)`
      : `${mail.sent} staff emailed`;
    flash(`Published ${drafts.length} shift${drafts.length === 1 ? '' : 's'} · ${emailMsg}.`);
  }, [weekShifts, empById, users, currentUser, weekLabel, weekYmds, ownedBusinesses, bizId, reload, flash]);

  const copyPreviousWeek = useCallback(async () => {
    const prevMonday = addDays(monday, -7);
    const prevYmds = Array.from({ length: 7 }, (_, i) => ymd(addDays(prevMonday, i)));
    const src = shifts.filter(s => s.business_id == bizId && s.is_deleted != 1 && s.status !== 'cancelled' && prevYmds.includes((s.shift_date || '').slice(0, 10)));
    if (src.length === 0) { flash('Previous week has no shifts to copy.'); return; }
    const now = NOW_ISO();
    for (const s of src) {
      await addRow('sched_shift', {
        business_id: bizId, employee_id: s.employee_id, location_id: s.location_id, unit_id: s.unit_id,
        shift_date: ymd(addDays(parseYmd((s.shift_date || '').slice(0, 10)), 7)),
        start_time: fmtTime(s.start_time) || null, end_time: fmtTime(s.end_time) || null,
        break_minutes: s.break_minutes || 0, role_label: s.role_label || null, notes: s.notes || null,
        status: 'draft', is_active: 1, is_deleted: 0, created_by: currentUser?.user_id ?? null, created_at: now, updated_at: now,
      }, 'shift_id');
    }
    await reload();
    flash(`Copied ${src.length} shift${src.length === 1 ? '' : 's'} from the previous week as drafts.`);
  }, [monday, shifts, bizId, currentUser, reload, flash]);

  // ---- templates ----
  const bizTemplates = useMemo(() => templates.filter(t =>
    // eslint-disable-next-line eqeqeq
    t.business_id == bizId && t.is_deleted != 1)
    .sort((a, b) => (a.name || '').localeCompare(b.name || '')), [templates, bizId]);

  const saveWeekAsTemplate = useCallback(async () => {
    const name = templateName.trim();
    if (!name) { flash('Enter a template name first.'); return; }
    const src = weekShifts;
    if (src.length === 0) { flash('This week has no shifts to save as a template.'); return; }
    const now = NOW_ISO();
    try {
      const tmpl = await addRow('sched_template', { business_id: bizId, name, is_active: 1, is_deleted: 0, created_by: currentUser?.user_id ?? null, created_at: now, updated_at: now }, 'template_id');
      const tid = tmpl.template_id;
      for (const s of src) {
        await addRow('sched_template_shift', {
          template_id: tid, business_id: bizId, employee_id: s.employee_id ?? null,
          location_id: s.location_id ?? null, unit_id: s.unit_id ?? null,
          day_of_week: dowOf((s.shift_date || '').slice(0, 10)),
          start_time: fmtTime(s.start_time) || null, end_time: fmtTime(s.end_time) || null,
          break_minutes: s.break_minutes || 0, role_label: s.role_label || null,
          is_active: 1, is_deleted: 0, created_by: currentUser?.user_id ?? null, created_at: now, updated_at: now,
        }, 'tmpl_shift_id');
      }
      setTemplateName('');
      await reload();
      flash(`Saved template “${name}” (${src.length} shift${src.length === 1 ? '' : 's'}).`);
    } catch (e: any) { flash(e?.message || 'Could not save template.'); }
  }, [templateName, weekShifts, bizId, currentUser, reload, flash]);

  const applyTemplate = useCallback(async () => {
    if (!applyTemplateId) { flash('Pick a template to apply.'); return; }
    const tid = Number(applyTemplateId);
    // eslint-disable-next-line eqeqeq
    const rows = templateShifts.filter(ts => ts.template_id == tid && ts.is_deleted != 1);
    if (rows.length === 0) { flash('That template has no shifts.'); return; }
    const now = NOW_ISO();
    try {
      for (const ts of rows) {
        const dow = Math.max(0, Math.min(6, Number(ts.day_of_week) || 0));
        await addRow('sched_shift', {
          business_id: bizId, employee_id: ts.employee_id ?? null,
          location_id: ts.location_id ?? null, unit_id: ts.unit_id ?? null,
          shift_date: weekYmds[dow], start_time: fmtTime(ts.start_time) || null, end_time: fmtTime(ts.end_time) || null,
          break_minutes: ts.break_minutes || 0, role_label: ts.role_label || null,
          status: ts.employee_id ? 'draft' : 'open', is_active: 1, is_deleted: 0,
          created_by: currentUser?.user_id ?? null, created_at: now, updated_at: now,
        }, 'shift_id');
      }
      await reload();
      flash(`Applied ${rows.length} shift${rows.length === 1 ? '' : 's'} to this week as drafts.`);
    } catch (e: any) { flash(e?.message || 'Could not apply template.'); }
  }, [applyTemplateId, templateShifts, bizId, weekYmds, currentUser, reload, flash]);

  const deleteTemplate = useCallback(async () => {
    if (!applyTemplateId) { flash('Pick a template to delete.'); return; }
    const t = bizTemplates.find(x => String(x.template_id) === applyTemplateId);
    try {
      await removeRow('sched_template', Number(applyTemplateId), 'template_id');
      setApplyTemplateId('');
      await reload();
      flash(`Deleted template${t ? ` “${t.name}”` : ''}.`);
    } catch (e: any) { flash(e?.message || 'Could not delete template.'); }
  }, [applyTemplateId, bizTemplates, reload, flash]);

  // ---- drag & drop (move a shift to another employee/day) ----
  const moveShift = useCallback(async (shiftId: number, targetEmpId: number | null, targetDate: string) => {
    setDragOverKey(''); setDragShiftId(null);
    const s = shifts.find(x => Number(x.shift_id) === Number(shiftId));
    if (!s) return;
    const sameEmp = (s.employee_id == null && targetEmpId == null) || Number(s.employee_id) === Number(targetEmpId);
    if (sameEmp && (s.shift_date || '').slice(0, 10) === targetDate) return; // no-op
    try {
      await saveRow('sched_shift', shiftId, { employee_id: targetEmpId, shift_date: targetDate, updated_at: NOW_ISO() }, 'shift_id');
      await reload();
    } catch (e: any) { flash(e?.message || 'Could not move shift.'); }
  }, [shifts, reload, flash]);

  // ---- swaps ----
  const pendingSwaps = useMemo(() => swaps.filter(s =>
    // eslint-disable-next-line eqeqeq
    s.business_id == bizId && s.is_deleted != 1 && (s.status === 'requested' || s.status === 'claimed')), [swaps, bizId]);
  // Shift ids with any pending swap — drives the manager grid "swap" badge.
  const pendingSwapShiftIds = useMemo(() => new Set(pendingSwaps.map(s => Number(s.shift_id))), [pendingSwaps]);

  const approveSwap = useCallback(async (swap: Record<string, any>) => {
    const now = NOW_ISO();
    try {
      // Reassign the shift: to the taker/bidder if set, else it becomes an open shift.
      const newEmp = swap.to_employee_id != null ? Number(swap.to_employee_id) : null;
      await saveRow('sched_shift', swap.shift_id, { employee_id: newEmp, status: newEmp ? 'published' : 'open', updated_at: now }, 'shift_id');
      await saveRow('sched_swap_request', swap.swap_id, { status: 'approved', decided_by: currentUser?.user_id ?? null, decided_at: now, updated_at: now }, 'swap_id');
      // Awarding a shift settles the competition: reject any other open bids/requests on it.
      const siblings = swaps.filter(w => Number(w.shift_id) === Number(swap.shift_id) && Number(w.swap_id) !== Number(swap.swap_id) &&
        w.is_deleted != 1 && (w.status === 'requested' || w.status === 'claimed'));
      for (const w of siblings) {
        try { await saveRow('sched_swap_request', w.swap_id, { status: 'rejected', decided_by: currentUser?.user_id ?? null, decided_at: now, updated_at: now }, 'swap_id'); } catch { /* ignore */ }
      }
      await reload();
      flash(siblings.length > 0 ? `Awarded — ${siblings.length} other bid${siblings.length === 1 ? '' : 's'} declined.` : 'Swap approved.');
    } catch (e: any) { flash(e?.message || 'Could not approve.'); }
  }, [swaps, currentUser, reload, flash]);

  const rejectSwap = useCallback(async (swap: Record<string, any>) => {
    const now = NOW_ISO();
    try {
      await saveRow('sched_swap_request', swap.swap_id, { status: 'rejected', decided_by: currentUser?.user_id ?? null, decided_at: now, updated_at: now }, 'swap_id');
      await reload();
      flash('Swap rejected.');
    } catch (e: any) { flash(e?.message || 'Could not reject.'); }
  }, [currentUser, reload, flash]);

  // ---- employee actions ----
  const giveAwayShift = useCallback(async (s: Record<string, any>) => {
    const now = NOW_ISO();
    try {
      await addRow('sched_swap_request', {
        shift_id: s.shift_id, business_id: s.business_id, from_employee_id: s.employee_id, to_employee_id: null,
        swap_type: 'giveaway', status: 'requested', is_deleted: 0, created_by: currentUser?.user_id ?? null, created_at: now, updated_at: now,
      }, 'swap_id');
      // The shift stays 'published'; the pending swap row marks it up-for-grabs.
      await reload();
      flash('Give-away requested — pending manager approval.');
    } catch (e: any) { flash(e?.message || 'Could not request.'); }
  }, [currentUser, reload, flash]);

  const takeShift = useCallback(async (s: Record<string, any>, meEmpIdForBiz: number) => {
    const now = NOW_ISO();
    try {
      // If there's an open giveaway on this shift, claim it; else create a pickup request.
      const existing = swaps.find(w => Number(w.shift_id) === Number(s.shift_id) && w.is_deleted != 1 && w.status === 'requested');
      if (existing) {
        await saveRow('sched_swap_request', existing.swap_id, { to_employee_id: meEmpIdForBiz, status: 'claimed', updated_at: now }, 'swap_id');
      } else {
        await addRow('sched_swap_request', {
          shift_id: s.shift_id, business_id: s.business_id, from_employee_id: s.employee_id ?? null, to_employee_id: meEmpIdForBiz,
          swap_type: 'pickup', status: 'claimed', is_deleted: 0, created_by: currentUser?.user_id ?? null, created_at: now, updated_at: now,
        }, 'swap_id');
      }
      await reload();
      flash('Requested to take this shift — pending manager approval.');
    } catch (e: any) { flash(e?.message || 'Could not take the shift.'); }
  }, [swaps, currentUser, reload, flash]);

  // Bid on a truly-open shift (multiple employees may bid; the manager awards one).
  const bidShift = useCallback(async (s: Record<string, any>, meEmpIdForBiz: number) => {
    const now = NOW_ISO();
    try {
      await addRow('sched_swap_request', {
        shift_id: s.shift_id, business_id: s.business_id, from_employee_id: null, to_employee_id: meEmpIdForBiz,
        swap_type: 'pickup', status: 'requested', is_deleted: 0, created_by: currentUser?.user_id ?? null, created_at: now, updated_at: now,
      }, 'swap_id');
      await reload();
      flash('Bid placed — the manager will pick who gets the shift.');
    } catch (e: any) { flash(e?.message || 'Could not place bid.'); }
  }, [currentUser, reload, flash]);

  // ---- time-off + availability (conflict sources) ----
  // Approved or still-requested time-off covering a given employee+date.
  const timeOffConflict = useCallback((empId: number, dateY: string) =>
    timeOff.find(t => t.is_deleted != 1 && Number(t.employee_id) === empId &&
      (t.status === 'approved' || t.status === 'requested') &&
      dateInRange(dateY, (t.start_date || '').slice(0, 10), (t.end_date || '').slice(0, 10))) || null,
    [timeOff]);
  const dowOf = (dateY: string) => (parseYmd(dateY).getDay() + 6) % 7; // 0=Mon
  const availUnavailable = useCallback((empId: number, dateY: string) =>
    availability.find(a => a.is_deleted != 1 && Number(a.employee_id) === empId &&
      Number(a.day_of_week) === dowOf(dateY) && a.is_available === 0) || null, [availability]);
  // Unified conflict used by the grid marker + shift editor.
  const dayConflict = useCallback((empId: number, dateY: string): { kind: 'off' | 'unavail'; text: string } | null => {
    const off = timeOffConflict(empId, dateY);
    if (off) return { kind: 'off', text: `${off.status} time off${off.reason ? ` (${off.reason})` : ''}` };
    if (availUnavailable(empId, dateY)) return { kind: 'unavail', text: 'marked unavailable this weekday' };
    return null;
  }, [timeOffConflict, availUnavailable]);

  const pendingTimeOff = useMemo(() => timeOff.filter(t =>
    // eslint-disable-next-line eqeqeq
    t.business_id == bizId && t.is_deleted != 1 && t.status === 'requested'), [timeOff, bizId]);

  const approveTimeOff = useCallback(async (t: Record<string, any>, approve: boolean) => {
    const now = NOW_ISO();
    try {
      await saveRow('sched_time_off', t.time_off_id, { status: approve ? 'approved' : 'rejected', decided_by: currentUser?.user_id ?? null, decided_at: now, updated_at: now }, 'time_off_id');
      await reload();
      flash(approve ? 'Time off approved.' : 'Time off rejected.');
    } catch (e: any) { flash(e?.message || 'Could not update.'); }
  }, [currentUser, reload, flash]);

  // ---- labor cost / overtime ----
  const empHourlyRate = useCallback((empId: number) => {
    const e = empById.get(empId);
    return e && e.pay_type === 'hourly' ? Number(e.pay_rate) || 0 : 0;
  }, [empById]);
  const weeklyCost = useCallback((empId: number) => Math.round(weeklyHours(empId) * empHourlyRate(empId) * 100) / 100, [weeklyHours, empHourlyRate]);
  const weekSummary = useMemo(() => {
    let hours = 0, cost = 0, worked = 0, ot = 0;
    bizEmployees.forEach(e => {
      const h = weeklyHours(Number(e.employee_id));
      if (h > 0) worked++;
      if (h > OT_THRESHOLD) ot++;
      hours += h; cost += weeklyCost(Number(e.employee_id));
    });
    return { hours: Math.round(hours * 10) / 10, cost: Math.round(cost * 100) / 100, worked, ot };
  }, [bizEmployees, weeklyHours, weeklyCost]);

  // ---- ICS export (employee) ----
  const downloadMyIcs = useCallback((rows: Record<string, any>[]) => {
    const ics = buildIcs(rows, s => {
      const biz = businesses.find(b => Number(b.business_id) === Number(s.business_id));
      return `Shift · ${biz?.busi_legal_name || 'Work'}${locLabel(s.location_id, s.unit_id) ? ' — ' + locLabel(s.location_id, s.unit_id) : ''}`;
    });
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'my-shifts.ics'; document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [businesses, locLabel]);

  // ---- My Schedule data ----
  const myShifts = useMemo(() => shifts
    .filter(s => s.is_deleted != 1 && s.employee_id != null && myEmployeeIds.has(Number(s.employee_id)) && s.status === 'published' && (s.shift_date || '').slice(0, 10) >= todayYmd())
    .sort((a, b) => (a.shift_date || '').localeCompare(b.shift_date || '') || fmtTime(a.start_time).localeCompare(fmtTime(b.start_time))), [shifts, myEmployeeIds]);

  // Businesses I'm an employee of, and my employee_id in each (for "take").
  const myBizToEmpId = useMemo(() => {
    const m = new Map<number, number>();
    myEmployeeRows.forEach(e => m.set(Number(e.business_id), Number(e.employee_id)));
    return m;
  }, [myEmployeeRows]);

  // Shift ids that have a pending give-away swap (nobody's claimed yet) → up for grabs.
  const givenAwayShiftIds = useMemo(() => new Set(swaps
    .filter(w => w.is_deleted != 1 && w.swap_type === 'giveaway' && w.status === 'requested')
    .map(w => Number(w.shift_id))), [swaps]);

  const openShiftsForMe = useMemo(() => shifts.filter(s => {
    if (s.is_deleted == 1) return false;
    if (!myBizToEmpId.has(Number(s.business_id))) return false;
    if ((s.shift_date || '').slice(0, 10) < todayYmd()) return false;
    const mine = myEmployeeIds.has(Number(s.employee_id));
    const isOpen = s.employee_id == null || s.status === 'open';
    const upForGrabs = givenAwayShiftIds.has(Number(s.shift_id));
    return !mine && (isOpen || upForGrabs);
  }).sort((a, b) => (a.shift_date || '').localeCompare(b.shift_date || '')), [shifts, myBizToEmpId, myEmployeeIds, givenAwayShiftIds]);

  const myPendingSwaps = useMemo(() => swaps.filter(s => s.is_deleted != 1 && (s.status === 'requested' || s.status === 'claimed') &&
    (myEmployeeIds.has(Number(s.from_employee_id)) || myEmployeeIds.has(Number(s.to_employee_id)))), [swaps, myEmployeeIds]);

  const myTimeOff = useMemo(() => timeOff
    .filter(t => t.is_deleted != 1 && myEmployeeIds.has(Number(t.employee_id)) && t.status !== 'cancelled')
    .sort((a, b) => (b.start_date || '').localeCompare(a.start_date || '')), [timeOff, myEmployeeIds]);

  // Businesses I can request time off from (I'm on their roster).
  const myBizOptions = useMemo<DropdownOption[]>(() => Array.from(myBizToEmpId.keys()).map(bid => {
    const b = businesses.find(x => Number(x.business_id) === bid);
    return { value: String(bid), label: b?.busi_legal_name || `Business ${bid}` };
  }), [myBizToEmpId, businesses]);

  useEffect(() => {
    if (!toForm.business_id && myBizOptions.length) setToForm(f => ({ ...f, business_id: myBizOptions[0].value }));
  }, [myBizOptions, toForm.business_id]);

  const requestTimeOff = useCallback(async () => {
    if (!toForm.start || !toForm.end) { flash('Pick a start and end date.'); return; }
    if (toForm.end < toForm.start) { flash('End date must be on or after the start date.'); return; }
    const empId = myBizToEmpId.get(Number(toForm.business_id));
    if (!empId) { flash('Choose a business.'); return; }
    const now = NOW_ISO();
    try {
      await addRow('sched_time_off', {
        business_id: Number(toForm.business_id), employee_id: empId,
        start_date: toForm.start, end_date: toForm.end, reason: toForm.reason.trim() || null,
        all_day: 1, status: 'requested', is_deleted: 0, created_by: currentUser?.user_id ?? null, created_at: now, updated_at: now,
      }, 'time_off_id');
      setToForm(f => ({ ...f, start: '', end: '', reason: '' }));
      await reload();
      flash('Time-off request submitted — pending manager approval.');
    } catch (e: any) { flash(e?.message || 'Could not submit.'); }
  }, [toForm, myBizToEmpId, currentUser, reload, flash]);

  const cancelTimeOff = useCallback(async (t: Record<string, any>) => {
    try { await saveRow('sched_time_off', t.time_off_id, { status: 'cancelled', updated_at: NOW_ISO() }, 'time_off_id'); await reload(); flash('Request cancelled.'); }
    catch (e: any) { flash(e?.message || 'Could not cancel.'); }
  }, [reload, flash]);

  // ---- employee availability ----
  useEffect(() => {
    if (!availBizId && myBizOptions.length) setAvailBizId(myBizOptions[0].value);
  }, [myBizOptions, availBizId]);
  const availEmpId = useMemo(() => myBizToEmpId.get(Number(availBizId)) || null, [myBizToEmpId, availBizId]);
  // Current employee's availability rows for the selected business, by weekday.
  const myAvailByDow = useMemo(() => {
    const m = new Map<number, Record<string, any>>();
    if (availEmpId != null) {
      availability.filter(a => a.is_deleted != 1 && Number(a.employee_id) === availEmpId)
        .forEach(a => m.set(Number(a.day_of_week), a));
    }
    return m;
  }, [availability, availEmpId]);
  const setDayAvailable = useCallback(async (dow: number, available: boolean) => {
    if (availEmpId == null || !availBizId) return;
    const now = NOW_ISO();
    const existing = myAvailByDow.get(dow);
    try {
      if (existing) {
        await saveRow('sched_availability', existing.availability_id, { is_available: available ? 1 : 0, updated_at: now }, 'availability_id');
      } else {
        await addRow('sched_availability', {
          business_id: Number(availBizId), employee_id: availEmpId, day_of_week: dow,
          is_available: available ? 1 : 0, is_active: 1, is_deleted: 0, created_by: currentUser?.user_id ?? null, created_at: now, updated_at: now,
        }, 'availability_id');
      }
      await reload();
    } catch (e: any) { flash(e?.message || 'Could not update availability.'); }
  }, [availEmpId, availBizId, myAvailByDow, currentUser, reload, flash]);

  if (!loaded) return <div className="sch-page"><div className="sch-empty">Loading schedule…</div></div>;

  return (
    <div className="sch-page">
      <div className="sch-head">
        <h2 className="sch-title"><FiCalendar /> Scheduling</h2>
        {(isManager && isEmployee) && (
          <div className="sch-modetabs">
            <button className={mode === 'manage' ? 'active' : ''} onClick={() => setMode('manage')}>Manage schedules</button>
            <button className={mode === 'mine' ? 'active' : ''} onClick={() => setMode('mine')}>My schedule</button>
          </div>
        )}
      </div>

      {toast && <div className="sch-toast">{toast}</div>}

      {mode === 'manage' && isManager ? (
        <>
          {/* Manager controls */}
          <div className="sch-controls">
            <label className="sch-field">
              <span>Business</span>
              <select value={selectedBizId} onChange={e => setSelectedBizId(e.target.value)} className="sch-input">
                {ownedBusinesses.map(b => <option key={b.business_id} value={String(b.business_id)}>{b.busi_legal_name || b.busi_operating_name || `Business ${b.business_id}`}</option>)}
              </select>
            </label>
            <div className="sch-weeknav">
              <button onClick={() => setWeekAnchor(ymd(addDays(monday, -7)))} title="Previous week"><FiChevronLeft /></button>
              <span className="sch-weeklabel">{weekLabel}</span>
              <button onClick={() => setWeekAnchor(ymd(addDays(monday, 7)))} title="Next week"><FiChevronRight /></button>
              <button className="sch-today" onClick={() => setWeekAnchor(todayYmd())}>Today</button>
            </div>
            <div className="sch-actions">
              <button className="sch-btn" onClick={copyPreviousWeek} title="Copy last week's shifts as drafts"><FiCopy /> Copy last week</button>
              <button className="sch-btn sch-btn-primary" onClick={publishWeek}><FiSend /> Publish week</button>
            </div>
          </div>

          {locationOptions.length === 0 && (
            <div className="sch-note">This business has no locations yet. Add locations/units and employees in DMS → Masters → Businesses to schedule shifts.</div>
          )}

          {/* Templates */}
          <div className="sch-templatebar">
            <span className="sch-tpl-label">Templates</span>
            <input type="text" className="sch-input sch-tpl-name" placeholder="Name this week…" value={templateName} onChange={e => setTemplateName(e.target.value)} />
            <button className="sch-btn" onClick={saveWeekAsTemplate}><FiSave /> Save week as template</button>
            <span className="sch-tpl-sep" />
            <select className="sch-input" value={applyTemplateId} onChange={e => setApplyTemplateId(e.target.value)}>
              <option value="">Apply a template…</option>
              {bizTemplates.map(t => <option key={t.template_id} value={String(t.template_id)}>{t.name}</option>)}
            </select>
            <button className="sch-btn" onClick={applyTemplate} disabled={!applyTemplateId}><FiCopy /> Apply to this week</button>
            <button className="sch-btn sch-btn-no" onClick={deleteTemplate} disabled={!applyTemplateId} title="Delete selected template"><FiTrash2 /></button>
          </div>

          {/* Week summary */}
          {bizEmployees.length > 0 && (
            <div className="sch-summary">
              <div className="sch-sum-item"><span className="sch-sum-num">{weekSummary.hours.toFixed(1)}</span><span className="sch-sum-lbl">scheduled hours</span></div>
              <div className="sch-sum-item"><span className="sch-sum-num">{money(weekSummary.cost)}</span><span className="sch-sum-lbl">est. labor cost</span></div>
              <div className="sch-sum-item"><span className="sch-sum-num">{weekSummary.worked}</span><span className="sch-sum-lbl">staff scheduled</span></div>
              {weekSummary.ot > 0 && <div className="sch-sum-item sch-sum-warn"><span className="sch-sum-num">{weekSummary.ot}</span><span className="sch-sum-lbl">over {OT_THRESHOLD}h (OT)</span></div>}
            </div>
          )}

          {/* Week grid */}
          <div className="sch-grid-scroll">
            <table className="sch-grid">
              <thead>
                <tr>
                  <th className="sch-emp-col">Employee</th>
                  {weekDates.map((d, i) => (
                    <th key={i} className={ymd(d) === todayYmd() ? 'sch-today-col' : ''}>
                      <div>{fmtDayLabel(d)}</div>
                      <div className="sch-cover">{dailyCoverage(ymd(d))} on</div>
                    </th>
                  ))}
                  <th className="sch-hrs-col">Hrs</th>
                </tr>
              </thead>
              <tbody>
                {bizEmployees.map(emp => {
                  const eid = Number(emp.employee_id);
                  return (
                    <tr key={eid}>
                      <td className="sch-emp-col">
                        <div className="sch-emp-name">{empName(emp)}</div>
                        {emp.position && <div className="sch-emp-pos">{emp.position}</div>}
                      </td>
                      {weekYmds.map(dy => {
                        const conf = dayConflict(eid, dy);
                        const cellKey = `${eid}:${dy}`;
                        return (
                        <td key={dy} className={`sch-cell ${conf ? (conf.kind === 'off' ? 'sch-cell-off' : 'sch-cell-unavail') : ''} ${dragOverKey === cellKey ? 'sch-cell-dragover' : ''}`} onClick={() => openNewShift(eid, dy)}
                          title={conf ? conf.text : undefined}
                          onDragOver={ev => { if (dragShiftId != null) { ev.preventDefault(); if (dragOverKey !== cellKey) setDragOverKey(cellKey); } }}
                          onDragLeave={() => { if (dragOverKey === cellKey) setDragOverKey(''); }}
                          onDrop={ev => { ev.preventDefault(); if (dragShiftId != null) moveShift(dragShiftId, eid, dy); }}>
                          {conf && <span className={`sch-off-tag ${conf.kind === 'unavail' ? 'unavail' : ''}`}>{conf.kind === 'off' ? 'off' : 'n/a'}</span>}
                          {shiftsForCell(eid, dy).map(s => (
                            <div key={s.shift_id} className={`sch-chip sch-chip-${s.status} ${dragShiftId === Number(s.shift_id) ? 'sch-chip-dragging' : ''}`}
                              draggable
                              onDragStart={() => setDragShiftId(Number(s.shift_id))}
                              onDragEnd={() => { setDragShiftId(null); setDragOverKey(''); }}
                              style={{ borderLeftColor: colorForLocation(s.location_id, s.unit_id) }}
                              onClick={ev => { ev.stopPropagation(); openEditShift(s); }}
                              title={`${locLabel(s.location_id, s.unit_id)} · ${shiftHours(s)}h${s.status !== 'published' ? ' · ' + s.status : ''} · drag to move`}>
                              <span className="sch-chip-time">{fmtTime(s.start_time)}–{fmtTime(s.end_time)}</span>
                              {s.status === 'draft' && <span className="sch-chip-badge">draft</span>}
                              {pendingSwapShiftIds.has(Number(s.shift_id)) && <span className="sch-chip-badge swap">swap</span>}
                            </div>
                          ))}
                          <button className="sch-cell-add" onClick={ev => { ev.stopPropagation(); openNewShift(eid, dy); }} title="Add shift"><FiPlus size={12} /></button>
                        </td>
                      ); })}
                      <td className="sch-hrs-col">
                        <div className="sch-hrs-val">{weeklyHours(eid).toFixed(1)}{weeklyHours(eid) > OT_THRESHOLD && <span className="sch-ot-badge" title={`Over ${OT_THRESHOLD}h`}>OT</span>}</div>
                        {empHourlyRate(eid) > 0 && <div className="sch-cost-val">{money(weeklyCost(eid))}</div>}
                      </td>
                    </tr>
                  );
                })}
                {/* Open shifts row */}
                <tr className="sch-open-row">
                  <td className="sch-emp-col"><div className="sch-emp-name">Open shifts</div><div className="sch-emp-pos">unassigned</div></td>
                  {weekYmds.map(dy => {
                    const cellKey = `open:${dy}`;
                    return (
                    <td key={dy} className={`sch-cell ${dragOverKey === cellKey ? 'sch-cell-dragover' : ''}`} onClick={() => openNewShift(null, dy)}
                      onDragOver={ev => { if (dragShiftId != null) { ev.preventDefault(); if (dragOverKey !== cellKey) setDragOverKey(cellKey); } }}
                      onDragLeave={() => { if (dragOverKey === cellKey) setDragOverKey(''); }}
                      onDrop={ev => { ev.preventDefault(); if (dragShiftId != null) moveShift(dragShiftId, null, dy); }}>
                      {shiftsForCell(null, dy).map(s => (
                        <div key={s.shift_id} className={`sch-chip sch-chip-open ${dragShiftId === Number(s.shift_id) ? 'sch-chip-dragging' : ''}`}
                          draggable
                          onDragStart={() => setDragShiftId(Number(s.shift_id))}
                          onDragEnd={() => { setDragShiftId(null); setDragOverKey(''); }}
                          onClick={ev => { ev.stopPropagation(); openEditShift(s); }}
                          title={`${locLabel(s.location_id, s.unit_id)} · drag to assign`}>
                          <span className="sch-chip-time">{fmtTime(s.start_time)}–{fmtTime(s.end_time)}</span>
                        </div>
                      ))}
                      <button className="sch-cell-add" onClick={ev => { ev.stopPropagation(); openNewShift(null, dy); }} title="Add open shift"><FiPlus size={12} /></button>
                    </td>
                  ); })}
                  <td className="sch-hrs-col" />
                </tr>
                {bizEmployees.length === 0 && (
                  <tr><td colSpan={9} className="sch-empty-row">No employees for this business. Add them in DMS → Masters → Businesses → Employees.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Swap inbox */}
          {pendingSwaps.length > 0 && (
            <div className="sch-swapbox">
              <h3><FiInbox /> Shift requests <span className="sch-count">{pendingSwaps.length}</span></h3>
              {pendingSwaps.map(sw => {
                const shift = shifts.find(s => Number(s.shift_id) === Number(sw.shift_id));
                const from = empById.get(Number(sw.from_employee_id));
                const to = sw.to_employee_id != null ? empById.get(Number(sw.to_employee_id)) : null;
                return (
                  <div className="sch-swap-row" key={sw.swap_id}>
                    <div className="sch-swap-desc">
                      <strong>{sw.swap_type === 'giveaway' ? 'Give-away' : sw.swap_type === 'pickup' ? 'Pick-up' : 'Trade'}</strong>
                      {shift && <> · {(shift.shift_date || '').slice(0, 10)} {fmtTime(shift.start_time)}–{fmtTime(shift.end_time)}</>}
                      <> · {empName(from)}{to ? ` → ${empName(to)}` : ' → (open)'}</>
                      <span className={`sch-swap-status ${sw.status}`}>{sw.status}</span>
                    </div>
                    <div className="sch-swap-actions">
                      <button className="sch-btn sch-btn-ok" onClick={() => approveSwap(sw)}><FiCheck /> Approve</button>
                      <button className="sch-btn sch-btn-no" onClick={() => rejectSwap(sw)}><FiX /> Reject</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Time-off inbox */}
          {pendingTimeOff.length > 0 && (
            <div className="sch-swapbox">
              <h3><FiInbox /> Time-off requests <span className="sch-count">{pendingTimeOff.length}</span></h3>
              {pendingTimeOff.map(t => (
                <div className="sch-swap-row" key={t.time_off_id}>
                  <div className="sch-swap-desc">
                    <strong>{empName(empById.get(Number(t.employee_id)))}</strong>
                    <> · {(t.start_date || '').slice(0, 10)}{(t.end_date || '').slice(0, 10) !== (t.start_date || '').slice(0, 10) ? ` → ${(t.end_date || '').slice(0, 10)}` : ''}</>
                    {t.reason && <> · <em>{t.reason}</em></>}
                    <span className="sch-swap-status">{t.status}</span>
                  </div>
                  <div className="sch-swap-actions">
                    <button className="sch-btn sch-btn-ok" onClick={() => approveTimeOff(t, true)}><FiCheck /> Approve</button>
                    <button className="sch-btn sch-btn-no" onClick={() => approveTimeOff(t, false)}><FiX /> Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        /* ---------------- My Schedule ---------------- */
        <div className="sch-mine">
          {!isEmployee ? (
            <div className="sch-empty">You're not on any staff roster yet. When a business adds you as an employee (matched by your email <strong>{currentUser?.email}</strong>) your shifts will appear here.</div>
          ) : (
            <>
              <section className="sch-mine-sec">
                <div className="sch-mine-sechead">
                  <h3><FiClock /> My upcoming shifts <span className="sch-count">{myShifts.length}</span></h3>
                  {myShifts.length > 0 && <button className="sch-btn sch-btn-sm" onClick={() => downloadMyIcs(myShifts)}><FiCalendar /> Add to calendar (.ics)</button>}
                </div>
                {myShifts.length === 0 ? <div className="sch-empty-row">No published upcoming shifts.</div> : (
                  <div className="sch-mine-list">
                    {myShifts.map(s => {
                      const biz = businesses.find(b => Number(b.business_id) === Number(s.business_id));
                      const pending = myPendingSwaps.find(w => Number(w.shift_id) === Number(s.shift_id));
                      return (
                        <div className="sch-mine-card" key={s.shift_id} style={{ borderLeftColor: colorForLocation(s.location_id, s.unit_id) }}>
                          <div className="sch-mine-when">{fmtDayLabel(parseYmd((s.shift_date || '').slice(0, 10)))} · {fmtTime(s.start_time)}–{fmtTime(s.end_time)} <span className="sch-mine-hrs">{shiftHours(s)}h</span></div>
                          <div className="sch-mine-meta">{biz?.busi_legal_name || 'Business'}{locLabel(s.location_id, s.unit_id) ? ` · ${locLabel(s.location_id, s.unit_id)}` : ''}{s.role_label ? ` · ${s.role_label}` : ''}</div>
                          {pending ? <span className="sch-mine-pending"><FiRepeat /> {pending.status === 'claimed' ? 'Pickup pending approval' : 'Give-away pending approval'}</span>
                            : <button className="sch-btn sch-btn-sm" onClick={() => giveAwayShift(s)}><FiRepeat /> Give away</button>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              <section className="sch-mine-sec">
                <h3><FiUsers /> Open shifts you can take <span className="sch-count">{openShiftsForMe.length}</span></h3>
                {openShiftsForMe.length === 0 ? <div className="sch-empty-row">Nothing available right now.</div> : (
                  <div className="sch-mine-list">
                    {openShiftsForMe.map(s => {
                      const biz = businesses.find(b => Number(b.business_id) === Number(s.business_id));
                      const meEmp = myBizToEmpId.get(Number(s.business_id))!;
                      const mineReq = swaps.find(w => Number(w.shift_id) === Number(s.shift_id) && w.is_deleted != 1 && myEmployeeIds.has(Number(w.to_employee_id)) && (w.status === 'claimed' || w.status === 'requested'));
                      const isGiveaway = givenAwayShiftIds.has(Number(s.shift_id));
                      // Count competing bids on a truly-open shift.
                      const bidCount = swaps.filter(w => Number(w.shift_id) === Number(s.shift_id) && w.is_deleted != 1 && w.swap_type === 'pickup' && w.status === 'requested').length;
                      return (
                        <div className="sch-mine-card" key={s.shift_id} style={{ borderLeftColor: colorForLocation(s.location_id, s.unit_id) }}>
                          <div className="sch-mine-when">{fmtDayLabel(parseYmd((s.shift_date || '').slice(0, 10)))} · {fmtTime(s.start_time)}–{fmtTime(s.end_time)} <span className="sch-mine-hrs">{shiftHours(s)}h</span></div>
                          <div className="sch-mine-meta">{biz?.busi_legal_name || 'Business'}{locLabel(s.location_id, s.unit_id) ? ` · ${locLabel(s.location_id, s.unit_id)}` : ''}{!isGiveaway && bidCount > 0 ? ` · ${bidCount} bid${bidCount === 1 ? '' : 's'}` : ''}</div>
                          {mineReq ? <span className="sch-mine-pending"><FiRepeat /> {isGiveaway ? 'Requested' : 'Bid placed'}</span>
                            : isGiveaway
                              ? <button className="sch-btn sch-btn-sm sch-btn-primary" onClick={() => takeShift(s, meEmp)}><FiCheck /> Take shift</button>
                              : <button className="sch-btn sch-btn-sm sch-btn-primary" onClick={() => bidShift(s, meEmp)}><FiCheck /> Bid for shift</button>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* Time off */}
              <section className="sch-mine-sec">
                <h3><FiCalendar /> Time off</h3>
                <div className="sch-to-form">
                  {myBizOptions.length > 1 && (
                    <label className="sch-field"><span>Business</span>
                      <select className="sch-input" value={toForm.business_id} onChange={e => setToForm(f => ({ ...f, business_id: e.target.value }))}>
                        {myBizOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </label>
                  )}
                  <label className="sch-field"><span>From</span><input type="date" className="sch-input" value={toForm.start} onChange={e => setToForm(f => ({ ...f, start: e.target.value }))} /></label>
                  <label className="sch-field"><span>To</span><input type="date" className="sch-input" value={toForm.end} onChange={e => setToForm(f => ({ ...f, end: e.target.value }))} /></label>
                  <label className="sch-field sch-to-reason"><span>Reason (optional)</span><input type="text" className="sch-input" value={toForm.reason} placeholder="e.g. Vacation" onChange={e => setToForm(f => ({ ...f, reason: e.target.value }))} /></label>
                  <button className="sch-btn sch-btn-primary sch-to-submit" onClick={requestTimeOff}><FiSend /> Request</button>
                </div>
                {myTimeOff.length > 0 && (
                  <div className="sch-to-list">
                    {myTimeOff.map(t => (
                      <div className="sch-to-row" key={t.time_off_id}>
                        <span className="sch-to-dates">{(t.start_date || '').slice(0, 10)}{(t.end_date || '').slice(0, 10) !== (t.start_date || '').slice(0, 10) ? ` → ${(t.end_date || '').slice(0, 10)}` : ''}</span>
                        {t.reason && <span className="sch-to-reasontxt">{t.reason}</span>}
                        <span className={`sch-swap-status ${t.status}`}>{t.status}</span>
                        {t.status === 'requested' && <button className="sch-btn sch-btn-sm sch-btn-no" onClick={() => cancelTimeOff(t)}>Cancel</button>}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Weekly availability */}
              <section className="sch-mine-sec">
                <div className="sch-mine-sechead">
                  <h3><FiRepeat /> My weekly availability</h3>
                  {myBizOptions.length > 1 && (
                    <select className="sch-input" value={availBizId} onChange={e => setAvailBizId(e.target.value)}>
                      {myBizOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  )}
                </div>
                <p className="sch-avail-hint">Days you mark unavailable will flag a warning for your manager when scheduling. Defaults to available.</p>
                <div className="sch-avail-grid">
                  {DAY_NAMES.map((dn, dow) => {
                    const avail = (myAvailByDow.get(dow)?.is_available ?? 1) !== 0;
                    return (
                      <div className="sch-avail-day" key={dow}>
                        <span className="sch-avail-dn">{dn}</span>
                        <div className="sch-avail-toggle">
                          <button className={avail ? 'on' : ''} onClick={() => setDayAvailable(dow, true)}>Available</button>
                          <button className={!avail ? 'off' : ''} onClick={() => setDayAvailable(dow, false)}>Unavailable</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </>
          )}
        </div>
      )}

      {/* Shift editor modal */}
      {editor && (
        <div className="sch-modal-overlay" onClick={() => setEditor(null)}>
          <div className="sch-modal" onClick={e => e.stopPropagation()}>
            <div className="sch-modal-head">
              <h3>{editor.shift_id ? 'Edit shift' : 'Add shift'}</h3>
              <button onClick={() => setEditor(null)}><FiX /></button>
            </div>
            <label className="sch-field"><span>Employee</span>
              <SearchableDropdown options={[{ value: '', label: '— Open shift (unassigned) —' }, ...employeeOptions]}
                value={editor.employee_id} onChange={v => setEditor(d => d ? { ...d, employee_id: v } : d)} placeholder="Open shift" />
            </label>
            {(() => {
              const conf = editor.employee_id ? dayConflict(Number(editor.employee_id), editor.shift_date) : null;
              return conf ? <div className="sch-conflict">⚠ {empName(empById.get(Number(editor.employee_id)))} is {conf.text} on this date.</div> : null;
            })()}
            <label className="sch-field"><span>Location / unit</span>
              <SearchableDropdown options={locationOptions} value={editor.location_id}
                onChange={v => setEditor(d => d ? { ...d, location_id: v } : d)} placeholder="Select location/unit" />
            </label>
            <div className="sch-field-row">
              <label className="sch-field"><span>Date</span>
                <input type="date" className="sch-input" value={editor.shift_date} onChange={e => setEditor(d => d ? { ...d, shift_date: e.target.value } : d)} />
              </label>
              <label className="sch-field"><span>Start</span>
                <input type="time" className="sch-input" value={editor.start_time} onChange={e => setEditor(d => d ? { ...d, start_time: e.target.value } : d)} />
              </label>
              <label className="sch-field"><span>End</span>
                <input type="time" className="sch-input" value={editor.end_time} onChange={e => setEditor(d => d ? { ...d, end_time: e.target.value } : d)} />
              </label>
              <label className="sch-field sch-field-narrow"><span>Break (min)</span>
                <input type="number" min="0" className="sch-input" value={editor.break_minutes} onChange={e => setEditor(d => d ? { ...d, break_minutes: e.target.value } : d)} />
              </label>
            </div>
            <label className="sch-field"><span>Role / note for this shift</span>
              <input type="text" className="sch-input" value={editor.role_label} placeholder="e.g. Opener, Cashier" onChange={e => setEditor(d => d ? { ...d, role_label: e.target.value } : d)} />
            </label>
            {!editor.shift_id && (
              <label className="sch-field"><span>Repeat weekly</span>
                <div className="sch-repeat">
                  <span>for</span>
                  <input type="number" min="1" max="52" className="sch-input sch-repeat-num" value={editor.repeat_weeks} onChange={e => setEditor(d => d ? { ...d, repeat_weeks: e.target.value } : d)} />
                  <span>week(s){Number(editor.repeat_weeks) > 1 ? ` — same weekday through ${fmtDayLabel(addDays(parseYmd(editor.shift_date), (Math.max(1, Number(editor.repeat_weeks)) - 1) * 7))}` : ''}</span>
                </div>
              </label>
            )}
            <div className="sch-modal-foot">
              {editor.shift_id && <button className="sch-btn sch-btn-no" onClick={() => deleteShift(editor.shift_id!)}><FiTrash2 /> Delete</button>}
              <div className="sch-modal-foot-right">
                <button className="sch-btn" onClick={() => setEditor(null)}>Cancel</button>
                <button className="sch-btn sch-btn-primary" onClick={saveShift}><FiCheck /> {editor.shift_id ? 'Save' : 'Add shift'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchedulingPage;
